'use strict';
// UMD Module boilerplate
(function (root, factory) {
  /*global define */

  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module.
    define([], factory);
  } else if (typeof module === 'object' && module.exports) {
    // Node. Does not work with strict CommonJS, but
    // only CommonJS-like environments that support module.exports,
    // like Node.
    module.exports = factory();
  } else {
    // Browser globals (root is window)
    root.PrettyNum = factory();
  }
  /* eslint-disable-next-line no-restricted-globals, no-undef*/
})(typeof self !== 'undefined' ? self : this, function () {
  // Welcome to the factory function for PrettyNum

  var INPUT_EXCEPTION_TEXT = 'User Entry failed to parse as number';

  /**
    Raises a number to a power of 10 without needing to use the decimal value of the number if possible.
    Example where this function helps; previously the following could happen:  1.1*Math.pow(10,2) = 110.00000000000001
    This function turns the above into 11*Math.pow(10,1)
    */

  var processEngTextNoDecimal = function (value, powToRaiseTo) {
    if (isNaN(value)) {
      return ' ';
    }
    var decrementer = 0;
    var powValue = powToRaiseTo;
    var modifiedValue = value;
    var valuePieces = value.split('.');
    if (valuePieces.length > 1) {
      decrementer = valuePieces[1].length;
      modifiedValue = value.replace('.', '');
    }
    powValue = powToRaiseTo - decrementer;
    if (powValue < 0) {
      modifiedValue = value;
      powValue = powToRaiseTo;
    }
    return modifiedValue * Math.pow(10, powValue);
  };

  //Checks if the number is in a range that matches an engineering metricPrefix
  //For example if metricPrefix is 'k', this function checks if the number is between 1000 and 1e6
  //Returns null if number not in range, otherwise returns formatted number
  //
  //metricPrefix: engineering metricPrefix of the band we want to check
  //bigMetricPrefix:  next biggest metricPrefix, in case of numbers like 999.9999 which get rounded up
  //smallestNumber: lowest number in the range.  For example if small metricPrefix is k, then this is 1000
  //numberToCheck: the number we actually want to check
  //significantDigits:  how many significant digits the returned number should have
  var numberInMetricPrefixRange = function (data) {
    var engineeringText;
    var metricPrefixToUse;
    //We need to use the precision-adjusted number, in case rounding brings it across a 1000x tier, like 999999 to 1000000.
    var numberToUse = data.isFixedDigits
      ? data.numberToCheck
      : Number.parseFloat(data.numberToCheck.toPrecision(data.significantDigits));
    // Clean up rounding on multipliers
    var sNum = data.smallestNumber * 1000;
    sNum =
      sNum >= 1
        ? sNum.toFixed(0)
        : sNum * 1000 >= 1
        ? (sNum * 1000).toFixed(0) / 1000
        : sNum * 1000000 >= 1
        ? (sNum * 1000000).toFixed(0) / 1000000
        : sNum;
    // Now we have numbers we can safely use
    if (numberToUse < sNum) {
      var engineeringValue;
      if (data.isFixedDigits) engineeringValue = (numberToUse / data.smallestNumber).toFixed(data.significantDigits);
      else {
        engineeringValue = (numberToUse / data.smallestNumber).toPrecision(data.significantDigits);
        // toPrecision will render (0.001599999).toPrecision(2) as 0.0016 rather than 0.0
        if (engineeringValue.includes('.') && engineeringValue.length > data.significantDigits + 1) {
          engineeringValue = Number.parseFloat(engineeringValue).toFixed(
            data.significantDigits - engineeringValue.split('.')[0].length
          );
        }
      }

      if (Number.parseFloat(engineeringValue) === 0) engineeringText = '0';
      else engineeringText = engineeringValue + data.metricPrefix;

      //if the orginal number was 100,000, after the multiplication it will be 100 which the toPrecision will convert to something like 1.02e+2
      //To avoid showing the customer something like 1.0e+2k, we have the code below
      var eMatch = engineeringText.match(/e/i);
      if (eMatch) {
        var significand = engineeringText.slice(0, eMatch.index);
        var exponent = engineeringText.slice(eMatch.index + 2, eMatch.index + 3);

        if (exponent === '3') {
          metricPrefixToUse = data.bigMetricPrefix;
          exponent = '0';
        } else {
          metricPrefixToUse = data.metricPrefix;
        }
        engineeringText = processEngTextNoDecimal(significand, exponent) + metricPrefixToUse;
      }
      if (data.isSpice && engineeringText.match(/.*M$/g)) {
        engineeringText = engineeringText.slice(0, -1) + 'Meg';
      }
      return engineeringText;
    } else {
      return null;
    }
  };

  //Remove extra zeroes.  Assumes numberString starts with number
  var removeZeroes = function (numberString) {
    //Must have numbers followed by a period followed by more numbers for this function to do anything
    var matchReturn = numberString.match(/^[+-]?[0-9]+\.[0-9]+/);
    if (matchReturn) {
      var numberWithZeroes = numberString.slice(0, matchReturn[0].length);
      var restOfString = numberString.slice(matchReturn[0].length);
      var numberWithoutZeroes = numberWithZeroes.replace(/0*$/, '');
      var remDot = numberWithoutZeroes.replace(/\.$/, ''); // removing the trailing dot
      return remDot + restOfString;
    } else {
      return numberString;
    }
  };

  var isValidNumericMetricString = function (textFromUser, regEx) {
    if (!textFromUser.match(regEx)) {
      return false;
    }
    var text = textFromUser.replace(regEx, '').toString();
    return text.match(/[0-9]$/g) || text.match(/\W+/g);
  };

  // The following delivers numbers without exponential notation.
  // It was taken from stackoverflow response by Jenny O'Reilly (based on solution by outis) to following post:
  //  https://stackoverflow.com/questions/1685680/how-to-avoid-scientific-notation-for-large-numbers-in-javascript
  var stringAtPrecision = function (num, digits) {
    var numStr = num.toPrecision(digits);
    if (numStr.indexOf('e') === -1) {
      return numStr;
    }

    var e;
    var negative;
    if (Math.abs(num) < 1.0) {
      e = parseInt(numStr.split('e-')[1]);
      if (e) {
        negative = num < 0;
        if (negative) num *= -1;
        num *= Math.pow(10, e - 1);
        numStr = '0.' + new Array(e).join('0') + num.toPrecision(digits).substring(2);
        if (negative) numStr = '-' + numStr;
      }
    } else {
      e = parseInt(numStr.split('e+')[1]);
      if (e > 20) {
        e -= 20;
        num /= Math.pow(10, e);
        numStr = num.toString() + new Array(e + 1).join('0');
      } else {
        negative = num < 0;
        if (negative) num *= -1;
        num /= Math.pow(10, e + 1);
        numStr = num.toPrecision(digits).slice(2);
        numStr = numStr + new Array(e - numStr.length + 2).join('0');
        if (negative) numStr = '-' + numStr;
      }
    }

    return numStr;
  };

  //This function takes a string with a metric metricPrefix and returns a number
  var metricStringToNumber = function (textFromUser, regEx, exponent, errorValue) {
    var number, pattern;
    //remove the metricPrefix
    var text = textFromUser.replace(regEx, '').toString();
    //check if rest of the part has digits and decimal
    if (text.match(/[0-9]$/g) || text.match(/\W+/g)) {
      //turn the number into numeric format
      pattern = parseFloat(text);
      //multiply by exponent
      var expo = Math.pow(10, exponent);
      number = expo * pattern;
      //Limit to 12 significant digits to avoid floating point errors
      //Otherwise you can get stuff like 30 * 1e-9 = 3.0000000000000004e-8
      var chopSomeDigitsOff = number.toPrecision(12);
      number = parseFloat(chopSomeDigitsOff);
    } else if (typeof errorValue === 'number') {
      return errorValue;
    } else {
      throw INPUT_EXCEPTION_TEXT;
    }
    return number;
  };

  // The following builds and returns the PrettyNum implementation
  return (function () {
    var PrettyNum = {}; // PrettyNum is an object, not a class. It doesn't need to be instantiated. Its members are functions

    PrettyNum.INFINITY = 1 * Math.pow(10, 60); //1e60
    PrettyNum.NEGINFINITY = -1 * Math.pow(10, 60); //-1e60

    /**
     * Converts user entry into a javascript number. All of the following convert into the JavaScript Number 3.4e7:
     *   '34000000', '3.4e7', '3.4E7', '34e6', '34E6','34M','0.034G','.034G','34Meg','34MEG'
     * @param text - string - User-supplied text
     * @param errorValue - number - Value to supply if the string cannot be interpreted as a number - Default: throw exception
     * @return number - The numeric value of the text string supplied
     */
    PrettyNum.userEntryToNumber = function (text, errorValue) {
      if (text !== null && text !== undefined && text !== '') {
        //var invalidUserEntry;
        //invalidUserEntry = false; // boolean value setting to false.
        var number;
        var patterninf = /^inf/i; // i is search with ignore case character and ^ is to search the start of a string, $ matches the end of the string
        var patternNeginf = /^-inf/i; // pattern for negative infinity

        var textToUse = text.replace(/\s+/g, '').replace(/\u2212/g,'-'); //clear whitespace, use hyphen-minus, not Unicode minus

        if (textToUse.match(patterninf)) {
          number = PrettyNum.INFINITY;
        } else if (textToUse.match(patternNeginf)) {
          number = PrettyNum.NEGINFINITY;
        } else if (textToUse === 'NaN') {
          number = Number.NaN;
        } else if (textToUse.match(/Meg$/gi)) {
          // checks if the last character is Meg
          number = metricStringToNumber(textToUse, /Meg$/gi, 6, errorValue);
        } else if (textToUse.match(/T$/g)) {
          number = metricStringToNumber(textToUse, /T$/g, 12, errorValue);
        } else if (textToUse.match(/G$/g)) {
          // checks if the last character is G
          number = metricStringToNumber(textToUse, /G$/g, 9, errorValue);
        } else if (textToUse.match(/M$/g)) {
          // checks if the last character is M
          number = metricStringToNumber(textToUse, /M$/g, 6, errorValue);
        } else if (textToUse.match(/k$/g)) {
          // checks if the last character is k
          number = metricStringToNumber(textToUse, /k$/g, 3, errorValue);
        } else if (textToUse.match(/m$/g)) {
          number = metricStringToNumber(textToUse, /m$/g, -3, errorValue);
        } else if (textToUse.match(/u$/g)) {
          number = metricStringToNumber(textToUse, /u$/g, -6, errorValue);
        } else if (textToUse.match(/μ$/g)) {
          number = metricStringToNumber(textToUse, /\u03BC$/g, -6, errorValue); // 03BC is the Greek μ symbol
        } else if (textToUse.match(/\xB5$/g)) {
          number = metricStringToNumber(textToUse, /\xB5$/g, -6, errorValue); // B5 is the Micro Symbol
        } else if (textToUse.match(/n$/g)) {
          number = metricStringToNumber(textToUse, /n$/g, -9, errorValue);
        } else if (textToUse.match(/p$/g)) {
          number = metricStringToNumber(textToUse, /p$/g, -12, errorValue);
        } else if (textToUse.match(/f$/g)) {
          number = metricStringToNumber(textToUse, /f$/g, -15, errorValue);
        } else if (textToUse.match(/a$/g)) {
          number = metricStringToNumber(textToUse, /a$/g, -18, errorValue);
        } else if (textToUse.match(/^[+-]?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?$/g)) {
          number = Number(parseFloat(textToUse));
        } else if (textToUse.match(/^[-+]?(?:\d*\.\d{0,20}|\d+)$/) || textToUse.match(/^[-+]?[0-9]*$/g)) {
          textToUse = parseFloat(textToUse);
          number = Number(textToUse);
        } else if (textToUse.match(/s$/g)) {
          number = textToUse;
        } else if (typeof errorValue === 'number') {
          number = errorValue;
        } else {
          throw INPUT_EXCEPTION_TEXT;
        } //end of else if chain
      } else if (typeof errorValue === 'number') {
        number = errorValue;
      } else {
        throw INPUT_EXCEPTION_TEXT;
      } //end of first if.. input validation if
      return number;
    };

    /**
     * Verifies that the supplied string can be interpreted as a number, i.e. that userEntryToNumber will not throw an exception.
     * @param text
     * @return boolean - True if the supplied string is numeric and false if it is not.
     */
    PrettyNum.isUserEntryNumeric = function (text) {
      if (typeof text === 'undefined' || text === null || text === '') return false;

      var patterninf = /^inf/i; // i is search with ignore case character and ^ is to search the start of a string, $ matches the end of the string
      var patternNeginf = /^-inf/i; // pattern for negative infinity
      var textToUse = text.replace(/\s+/g, '').replace(/\u2212/g,'-'); //clear whitespace, use hyphen-minus, not Unicode minus
      return !!(
        textToUse.match(patterninf) ||
        textToUse.match(patternNeginf) ||
        textToUse === 'NaN' ||
        isValidNumericMetricString(textToUse, /Meg$/gi) ||
        isValidNumericMetricString(textToUse, /[TGMkmu\u03BC\xB5npfa]$/g) || // 03BC is Greek μ and B5 is the Micro Symbol
        textToUse.match(/^[+-]?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?$/g) ||
        textToUse.match(/^[-+]?(?:\d*\.\d{0,20}|\d+)$/) ||
        textToUse.match(/^[-+]?[0-9]*$/g)
      );
    };

    /**
     * Converts a numeric value into a display string.
     * @param number - number - The number to display
     * @param format - string - "Standard", "Scientific", "Engineering", or "Spice"
     *   Standard - converts number into standard format: 34000000
     *   Scientific - converts number into scientific format: 3.4e7
     *   Engineering - converts number into engineering format: 34M
     *   Spice - converts number into SPICE format, identical to engineering format except that a million is represented by Meg rather than M
     * @param digits - number - The number of digits to display
     * @param options - hash -
     *   showZeroes - boolean - if true, show trailing zeroes to the requisite number of digits
     *   supplySpace - boolean - if true, supply a space between number and multiplier for Engineering, suitable for appending units to resulting string
     *   fixedDigits - (default: false) - If false, the digits parameter sets the significant digits to display.  If true, the digits parameter sets the number of digits after the decimal
     *   minMetricPrefix - (default: "") - In some cases we will have a very small number that we would prefer to represent as zero, rather than as a small nonzero number.
     *      Example 1: instead of converting a number to 314f, if we pass in a mix metric prefix of 'm', this would convert to 0.
     *      Example 2: instead of converting a number to 314f, a min metric prefix of 'p' would convert this to 0.314p.
     * @returns A display string for the supplied number.
     */
    PrettyNum.numberToUserDisplay = function (number, format, digits, options) {
      var textToDisplay, absNumber, sign, engineeringText, scientificText, floatingText, smallestNumber, metricPrefix;
      var metricPrefixList = ['a', 'f', 'p', 'n', 'u', 'm', '', 'k', 'M', 'G', 'T', 'P'];
      var minMetricPrefixIndex = 0;
      var suppressZeroes = !options || !options.showZeroes || options.showZeroes === false;
      var isFixedDigits = options && options.fixedDigits ? true : false;
      var supplySpace = options && options.supplySpace ? true : false;

      if (
        options &&
        options.minMetricPrefix &&
        (metricPrefixList.includes(options.minMetricPrefix) || options.minMetricPrefix === 'Meg')
      ) {
        minMetricPrefixIndex = metricPrefixList.indexOf(
          options.minMetricPrefix === 'Meg' ? 'M' : options.minMetricPrefix
        );
      } else if (format === 'Spice') {
        minMetricPrefixIndex = metricPrefixList.indexOf('f');
      }

      //check for special numeric values and our "large number for infinity" hack.
      if (isNaN(number)) return 'NaN';
      if (number === Infinity || number >= PrettyNum.INFINITY) return 'Inf';
      else if (number === -Infinity || number <= PrettyNum.NEGINFINITY) return '-Inf';
      else if (number === '-0') {
        return '-0';
      }

      //Engineering notation
      if (format === 'Engineering' || format === 'Spice') {
        absNumber = Math.abs(number);
        sign = number / absNumber;
        smallestNumber = 1e-18;

        if (absNumber < smallestNumber) {
          if (absNumber < 1e-60){ 
            return '0'; 
          } else {
          // Too small for engineering notation
            return PrettyNum.numberToUserDisplay(number, 'Scientific', digits, options);
          }
        } else {
          var loopFinished = true;
          for (var i = 0; i < metricPrefixList.length - 1; i++) {
            metricPrefix = metricPrefixList[i];

            //Check if number is a number that would normally have metricPrefixList[i] after it.  If so, return formatted number.
            if (i >= minMetricPrefixIndex) {
              engineeringText = numberInMetricPrefixRange({
                metricPrefix: metricPrefix,
                bigMetricPrefix: metricPrefixList[i + 1],
                smallestNumber: smallestNumber,
                numberToCheck: absNumber,
                significantDigits: digits,
                isSpice: format === 'Spice',
                isFixedDigits: isFixedDigits,
              });
            }

            if (engineeringText) {
              loopFinished = false;
              break;
            } else {
              smallestNumber *= 1000;
            }
          }
          if (loopFinished) {
            // Too large for engineering notation
            return PrettyNum.numberToUserDisplay(number, 'Scientific', digits, options);
          }
        }
        if (suppressZeroes) {
          engineeringText = removeZeroes(engineeringText);
        }
        if (sign < 0 && engineeringText !== '0') {
          engineeringText = '-' + engineeringText;
        }
        textToDisplay = engineeringText;
      }
      //Scientific notation
      else if (format === 'Scientific') {
        scientificText = number
          .toExponential(digits - 1)
          .replace('e+', 'e')
          .replace('e0', '');

        if (suppressZeroes) {
          scientificText = removeZeroes(scientificText);
        } else if (isFixedDigits) {
          var indexOfE = scientificText.indexOf('e');
          if (indexOfE >= 0)
            scientificText =
              Number.parseFloat(scientificText.slice(0, indexOfE)).toFixed(digits) + scientificText.slice(indexOfE);
          else scientificText = Number.parseFloat(scientificText).toFixed(digits);
        }
        textToDisplay = scientificText;
      }
      //Floating point notation
      else {
        if (isFixedDigits) floatingText = number.toFixed(digits);
        else floatingText = stringAtPrecision(number, digits);

        if (suppressZeroes) floatingText = removeZeroes(floatingText);

        textToDisplay = floatingText;
      }

      if (supplySpace && metricPrefix && textToDisplay.includes(metricPrefix))
        textToDisplay =
          textToDisplay.slice(0, textToDisplay.indexOf(metricPrefix)) +
          ' ' +
          textToDisplay.slice(textToDisplay.indexOf(metricPrefix));
      else if (supplySpace) textToDisplay += ' ';

      return textToDisplay;
    };

    /**
     * Convert a numeric value into an engineering display string with 3 significant digits and without trailing zeroes
     * @param number - number - The number to display
     * @returns A display string for the supplied number.
     */
    PrettyNum.eng3 = function (number) {
      return PrettyNum.numberToUserDisplay(number, 'Engineering', 3);
    };
    /**
     * Convert a numeric value into an engineering display string with 3 significant digits and without trailing zeroes
     * @param number - number - The number to display
     * @returns A display string for the supplied number.
     */
    PrettyNum.eng4 = function (number) {
      return PrettyNum.numberToUserDisplay(number, 'Engineering', 4);
    };

    return PrettyNum;
  })();
});
